import clsx from "clsx";
import React from "react";
import BranchLink from "web/react/components/branch-link/branch-link";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import browser from "web/script/utils/browser";
import { NotQuiteAURL } from "web/script/utils/url";
import styles from "./send-to-app.module.css";

export interface SendToAppProps {
    title?: string;
    url: string;
    analyticsEventLabel: string;
    productImageURL?: string | null;
    downloadCTA: string;
    extraContent?: () => JSX.Element | null;
    className?: string;
    appDeepLinkURL?: URL | NotQuiteAURL;
    financialIncentiveWebviewToken?: string;
    animatedModal?: boolean;
    onCtaClick?: React.EventHandler<React.MouseEvent>;
    showShippingDisclaimer?: boolean;
}

export default function SendToApp({
    title,
    productImageURL,
    analyticsEventLabel,
    url,
    downloadCTA,
    extraContent,
    className,
    appDeepLinkURL: appDeepLinkURL,
    financialIncentiveWebviewToken,
    onCtaClick,
    showShippingDisclaimer,
}: SendToAppProps): JSX.Element {
    const content = extraContent && extraContent();
    const useDesignSystem = environment.getFeature("eac_show_web2app_modal_redesign");

    return (
        <div className={clsx(className, styles.content)}>
            {title &&
                (useDesignSystem ? (
                    <Heading textStyle={"title-2"} align={"center"} as="h1">
                        {title}
                    </Heading>
                ) : (
                    <h1
                        className={clsx(styles.title, {
                            [styles.titleLargerMargin]: !content,
                            [styles.titleSmallerMargin]: content,
                        })}
                    >
                        {title}
                    </h1>
                ))}
            <div
                className={clsx(styles.imgContainer, {
                    [styles.imgContainerSmallerMargin]: !content,
                    [styles.imgContainerLargerMargin]: content,
                })}
            >
                {productImageURL && <img src={productImageURL} className={styles.productImg} />}
                <div
                    role="presentation"
                    className={clsx(styles.img, {
                        [styles.smallImg]: productImageURL != null,
                        [styles.largeImg]: productImageURL == null,
                        [styles.iosImg]: browser.ios,
                        [styles.androidImg]: browser.android,
                    })}
                />
            </div>
            {showShippingDisclaimer && (
                <Text
                    textStyle={"caption-1"}
                    className={styles.shippingDisclaimer}
                    color={"secondary"}
                >
                    {gettext("product_card.app_overlay_block.shipping_costs.label")}
                </Text>
            )}
            <div className={styles.buttonWrapper}>
                <BranchLink
                    title={downloadCTA}
                    branchLink={url}
                    eventLabel={analyticsEventLabel}
                    appDeepLinkURL={appDeepLinkURL}
                    financialIncentiveWebviewToken={financialIncentiveWebviewToken}
                    primaryButton
                    beforeOnClick={onCtaClick}
                />
            </div>
            {extraContent && extraContent()}
        </div>
    );
}
